import { ActionMap, createMsg } from 'src/core/engine/actionCreators';
import { map, tap } from 'rxjs';
import { setContext } from '@apollo/client/link/context';
import { EMPTY, Observable, of } from 'rxjs';
import { ofType } from 'src/core/engine/rxjs/ofType';
import { StorageActions, StorageMsg } from 'src/core/engine/storage/storage.actions';
import { EpicDeps, RegisterItem } from 'src/core/engine';

import { COOKIE_NAME } from './index';

export type CurrencyState = {
    code: string | null;
};

export const initialState: CurrencyState = {
    code: null,
};

export type Messages = {
    ['Currency.Set']: string;
    ['Currency.Hydrate']: string;
};

export function currencyReducer(state = initialState, action: CurrencyActions): CurrencyState {
    switch (action.type) {
        case 'Currency.Hydrate': {
            return {
                ...state,
                code: action.payload,
            };
        }
        case 'Currency.Set': {
            return {
                ...state,
                code: action.payload,
            };
        }
        default: {
            return state;
        }
    }
}

export const CurrencyMsg = createMsg<Messages>();
export type TypeMap = ActionMap<Messages>;
export type CurrencyActions = TypeMap[keyof TypeMap];
export type AppendState<T = {}> = T & { currency: CurrencyState };

const NAME = 'currency';

export function currencyRegister(epics) {
    return {
        epics: [...epics],
        reducers: { [NAME]: currencyReducer },
        name: NAME,
    };
}

export type CurrencyBrowserApi = {
    link: any;
    register: () => RegisterItem;
    appendHeader: (prevContext: Record<string, any>) => Record<string, any>;
};

export function createCurrencyBrowser(): CurrencyBrowserApi {
    // const bh = new BehaviorSubject({});
    let curr: string | null = null;
    const register = () => currencyRegister([setEpic, hydrateEpic]);

    /**
     * This will handle hydrating the currency code once
     * on initial payload
     * @param _action$
     * @param _state
     * @param deps
     */
    function hydrateEpic(_action$, _state, deps: EpicDeps) {
        const prev = deps.cookieStorage.get(COOKIE_NAME);
        if (prev && typeof prev === 'string' && prev.length === 3) {
            curr = prev;
            return of(CurrencyMsg('Currency.Hydrate', prev));
        }
        return EMPTY;
    }

    /**
     * This will handle setting the local value + as a cookie
     * @param action$
     */
    function setEpic(action$: Observable<any>) {
        return action$.pipe(
            ofType<CurrencyActions>('Currency.Set'),
            tap(({ payload }) => {
                curr = payload;
            }),
            map(({ payload }) => {
                return StorageMsg(StorageActions.SetCookie, { key: COOKIE_NAME, value: payload });
            }),
        );
    }

    /**
     * This is the apollo link that will add the content-currency
     * header if a currency is set.
     */
    function link(_req, prevContext) {
        if (curr) {
            return {
                headers: {
                    ...prevContext.headers,
                    [COOKIE_NAME]: curr,
                },
            };
        }
        return {};
    }

    return {
        appendHeader: link.bind(null, null),
        link: setContext(link),
        register,
    };
}
